/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  computed,
  ref,
  useRouter,
  useContext,
} from '@nuxtjs/composition-api';
import { debounce } from 'lodash-es';
import cartGetters, { getItemSku, getProduct, getConfiguredVariant } from '~/modules/checkout/getters/cartGetters';
import {
  useUiNotification,
  useExternalCheckout,
  useImage,
  useProduct,
} from '~/composables';
import { useCart } from '~/modules/checkout/composables/useCart';
import { useUser } from '~/modules/customer/composables/useUser';
import type { UseCartViewInterface } from '~/modules/checkout/composables/useCartView/useCartView';
import type { ConfigurableCartItem, BundleCartItem, CartItemInterface } from '~/modules/GraphQL/types';
import { ProductStockStatus } from '~/modules/GraphQL/types';
import { useOmnibusPrice } from '~/modules/catalog/product/composables/useOmnibusPrice';
import { isProductConfigurable } from '~/modules/catalog/product/getters/productGetters';
import { getValueFromTable } from '~/modules/catalog/product/helpers/stockHelpers';

/**
 * Allows loading and manipulating cart view.
 *
 * See the {@link UseCartViewInterface} for a list of methods and values available in this composable.
 */
export function useCartView(): UseCartViewInterface {
  const FREE_DELIVERY = 1;
  const { localePath, i18n } = useContext();
  const { initializeCheckout } = useExternalCheckout();
  const { getMagentoImage, imageSizes } = useImage();
  const router = useRouter();
  const { getProductPath } = useProduct();
  const { omnibusPrices } = useOmnibusPrice();
  const {
    cart,
    removeItem,
    updateItemQty,
    loading,
  } = useCart();
  const { isAuthenticated } = useUser();
  const { send: sendNotification, notifications } = useUiNotification();

  const products = computed(() => cartGetters
    .getItems(cart.value)
    .filter(Boolean)
    .map((item) => {
      const { sku: currentProductSku } = cartGetters.isProductConfigurable(item)
        ? cartGetters.getConfiguredVariant(item as ConfigurableCartItem)
        : item.product;
      return {
        ...item,
        product: {
          ...item.product,
          ...[(item as ConfigurableCartItem).configured_variant ?? {}],
          original_sku: item.product.sku,
          omnibus_price: omnibusPrices?.value ? omnibusPrices.value[currentProductSku] : '',
        },
      };
    }));
  const totals = computed(() => cartGetters.getTotals(cart.value));
  const discount = computed(() => -cartGetters.getDiscountAmount(cart.value));
  const totalItems = computed(() => cartGetters.getTotalItems(cart.value));
  const getAttributes = (product: ConfigurableCartItem) => product.configurable_options || [];
  const getBundles = (product: BundleCartItem) => product.bundle_options?.map((b) => b.values).flat() || [];
  const isRemoveModalVisible = ref(false);
  const itemToRemove = ref<CartItemInterface>();
  const isAmastyQuoteItem = computed(() => cartGetters.isAmastyQuoteItem(cart.value));

  const maxDeliveryTime = computed(() => {
    const deliveryTimes = products.value.map((product) => {
      // @ts-ignore
      const isConfigurable = isProductConfigurable(product.product);

      // @ts-ignore
      const deliveryTime = isConfigurable
        ? Number.parseInt(product.product[0].delivery_time, 10)
        // @ts-ignore
        : Number.parseInt(product.product.delivery_time, 10);

      return Number.isNaN(deliveryTime) ? null : deliveryTime;
    }).filter((time): time is number => time !== null);

    return getValueFromTable(Math.max(...deliveryTimes), i18n);
  });

  const leftForFreeDelivery = computed(() => {
    const subtotal = totals.value?.subtotal ?? 0;
    const currentDiscount = discount.value ?? 0;

    const price = subtotal + currentDiscount;
    return Math.max(0, FREE_DELIVERY - price);
  });
  const isFreeDelivery = computed(() => leftForFreeDelivery.value <= 0);

  const crossSellProducts = computed(() => {
    const cartItems = cartGetters.getItems(cart.value);
    // Get all the SKUs of the products in the cart
    const productsSkusInCart = new Set(cartItems.map((item) => getItemSku(item)));

    const cartCrossSellSKUs:string[] = cartItems.flatMap((item) => {
      const crossSellFromSimpleProduct = getProduct(item)?.crosssell_products || [];
      const crossSellFromConfiguredVariant = getConfiguredVariant(item as ConfigurableCartItem)?.crosssell_products || [];
      // Flatten and extract the 'sku' value from each cross-sell product
      return [
        ...crossSellFromSimpleProduct.map((prod) => prod.sku),
        ...crossSellFromConfiguredVariant.map((prod) => prod.sku),
      ];
    });
    // Filter out the products that are already in the cart
    const filteredCrossSellProducts = cartCrossSellSKUs.filter(
      (sku) => !productsSkusInCart.has(sku),
    );
    // Remove duplicates
    return [...new Set(filteredCrossSellProducts)];
  });

  const goToCheckout = async () => {
    const redirectUrl = initializeCheckout({ baseUrl: '/checkout/user-account' });
    await router.push(localePath(redirectUrl));
  };

  const showRemoveItemModal = ({ product }: { product: CartItemInterface }) => {
    if (notifications.value.length > 0) {
      notifications.value[0].dismiss();
    }

    isRemoveModalVisible.value = true;
    itemToRemove.value = product;
  };

  const removeItemAndSendNotification = async (product: CartItemInterface) => {
    await removeItem({ product });
    isRemoveModalVisible.value = false;

    sendNotification({
      id: Symbol('product_removed'),
      message: i18n.t('x was successfully removed from the basket', {
        x: cartGetters.getItemName(product),
      }) as string,
      type: 'success',
      icon: 'check',
      persist: false,
      title: 'Product removed',
    });
    // await loadCart();
  };

  const removeAllItems = async () => {
    for (const product of products.value) {
      // eslint-disable-next-line no-await-in-loop
      await removeItem({ product });
    }

    sendNotification({
      id: Symbol('products_removed'),
      message: i18n.t('All products have been successfully removed from the shopping cart.') as string,
      type: 'success',
      icon: 'check',
      persist: false,
      title: 'Products removed',
    });
  };

  const delayedUpdateItemQty = debounce(
    (params) => updateItemQty(params),
    500,
  );

  const isInStock = (product: CartItemInterface) => cartGetters.getStockStatus(product) === ProductStockStatus.InStock;

  return {
    showRemoveItemModal,
    removeAllItems,
    removeItemAndSendNotification,
    sendNotification,
    delayedUpdateItemQty,
    goToCheckout,
    getAttributes,
    getBundles,
    isInStock,
    getMagentoImage,
    getProductPath,
    loading,
    isAuthenticated,
    products,
    isRemoveModalVisible,
    itemToRemove,
    totals,
    totalItems,
    imageSizes,
    discount,
    cartGetters,
    isAmastyQuoteItem,
    leftForFreeDelivery,
    isFreeDelivery,
    FREE_DELIVERY,
    maxDeliveryTime,
    crossSellProducts,
  };
}

export default useCartView;
export * from './useCartView';
